import toastr from 'react-hot-toast';
import { useInfiniteQuery, useQueryClient } from 'react-query';
import {
  generateIncludeQuery,
  generatePaginationQuery,
} from 'utilities/hooks/urlGeneration';
import { fetchWrapper } from 'utils/fetchWrapper';
import { CACHE_AND_STALE_TIME } from 'constants/index';
import { createJoinedSparks } from 'helpers/spark';
import type { SparkResponse } from 'types/Spark/Spark';
import type { JoinedSparks } from 'types/Spark/JoinedSpark';
import { useWorkspaceStore } from 'stores';
import React from 'react';
import { SparkFilterType } from 'components/Providers/ContextProviders/SparkSearchContextProviderV2';

type SparkQueryFilters = {
  url?: string;
};

const getSparks = (
  currentWorkspaceID: string | null | undefined,
  sparkQueryFilters: SparkQueryFilters | undefined,
  pageParam: any
) => {
  if (!currentWorkspaceID) {
    Promise.reject('workspace ID not found');
    return;
  }

  const { url: filterURL } = sparkQueryFilters || {};

  const url = `${filterURL}&${generatePaginationQuery({
    pageNumber: pageParam,
    pageSize: 10,
  })}&${generateIncludeQuery([
    'sparkCategories',
    'marketplaceAccount',
    'sparkImages',
    'sparkRatings',
    'sparkFavourites',
    'sparkBillingType',
  ])}`;

  return fetchWrapper<SparkResponse>({
    url: url,
    method: 'GET',
  });
};

export function useInfiniteSparksQuery(sparkQueryFilters?: SparkQueryFilters) {
  const { url } = sparkQueryFilters || {};
  const queryClient = useQueryClient();
  const currentWorkspaceID = useWorkspaceStore(
    React.useCallback((state) => state.currentWorkspaceID, [])
  );
  const queryResult = useInfiniteQuery(
    ['sparks', sparkQueryFilters],
    ({ pageParam = 1 }) =>
      getSparks(currentWorkspaceID, sparkQueryFilters, pageParam),
    {
      getNextPageParam: (lastPage, allPages) => {
        return lastPage?.data?.length ? allPages?.length + 1 : undefined;
      },
      onError: (error) => {
        if (error instanceof Error) {
          toastr.error(`failed to Fetch Sparks: ${error.message}`);
        }
      },
      enabled: !!currentWorkspaceID,
      ...CACHE_AND_STALE_TIME,
    }
  );

  React.useEffect(() => {
    return () => {
      queryClient.cancelQueries(['sparks', url]);
    };
  }, [queryClient, url]);

  const { data: sparkResponse } = queryResult;
  const joinedSparks = sparkResponse?.pages.reduce<JoinedSparks[]>(
    (acc, page) => {
      if (!page?.data) {
        return acc;
      }
      return [...acc, ...(createJoinedSparks(page) || [])];
    },
    []
  );

  return {
    joinedSparks,
    ...queryResult,
  };
}
